import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Role } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/Identity/role';
import { MxIdentityService } from '../../services';

interface SzTab {
  label: string;
  icon: string;
  link: string;
  match: string[];
}

/**
 * Mobil alsó fülsor a prototípus (M10–M16) szerint: 4 fő cél szerepkörönként. Csak 768 px alatt látszik (CSS).
 */
@Component({
  standalone: false,
  selector: 'sz-bottom-nav',
  template: `
    <nav class="sz-bnav" *ngIf="tabs.length">
      <a *ngFor="let t of tabs" class="sz-bnav-item" [class.sz-bnav-item-active]="isActive(t)" [routerLink]="t.link">
        <span class="sz-icon sz-icon-24">{{ t.icon }}</span>
        <span class="sz-bnav-label">{{ t.label }}</span>
      </a>
    </nav>
  `,
  styles: [
    `
      :host { display: none; }
      @media (max-width: 767px) {
        :host { display: block; position: fixed; left: 0; right: 0; bottom: 0; z-index: 30; }
      }
      .sz-bnav { height: 64px; padding-bottom: env(safe-area-inset-bottom); background: #fff; border-top: 1px solid #DCE5E3; display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; }
      .sz-bnav-item { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; color: #5A6B68; text-decoration: none; font: 600 11px Figtree, sans-serif; }
      .sz-bnav-item-active { color: #0B6E6A; }
      .sz-bnav-item-active .sz-icon { font-variation-settings: 'FILL' 1; }
    `,
  ],
})
export class SzBottomNavComponent implements OnInit, OnDestroy {
  tabs: SzTab[] = [];
  url = '';
  private subs = new Subscription();

  constructor(
    private identityService: MxIdentityService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.url = this.router.url;
    this.subs.add(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => (this.url = this.router.url)));
    this.subs.add(this.identityService.account.subscribe(() => this.build()));
    this.build();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  isActive(t: SzTab): boolean {
    const path = (this.url || '').split('?')[0];
    if (t.link === '/') return path === '/' || path === '';
    return t.match.some((m) => path === m || path.startsWith(m + '/') || path.startsWith(m + '?'));
  }

  private build() {
    const role = Number(this.identityService.accountValue?.role);
    switch (role) {
      case Role.Admin:
      case Role.Administrator:
        this.tabs = [
          { label: 'Kezdőlap', icon: 'home', link: '/', match: ['/'] },
          { label: 'Reklamáció', icon: 'report', link: '/claims', match: ['/claims'] },
          { label: 'Ügyfelek', icon: 'store', link: '/subscriptions', match: ['/subscriptions'] },
          { label: 'Szirom Pont', icon: 'stars', link: '/loyalty/members', match: ['/loyalty'] },
        ];
        break;
      case Role.Pharmacist:
        this.tabs = [
          { label: 'Elérhető', icon: 'swap_horiz', link: '/work-schedule/available', match: ['/work-schedule/available'] },
          { label: 'Sajátjaim', icon: 'event_available', link: '/work-schedule', match: ['/work-schedule'] },
          { label: 'Hírek', icon: 'newspaper', link: '/posts/all', match: ['/posts'] },
        ];
        break;
      case Role.LogisticsUser:
        this.tabs = [
          { label: 'Reklamáció', icon: 'report', link: '/claims', match: ['/claims'] },
          { label: 'Vállalások', icon: 'bar_chart', link: '/commitment-documents', match: ['/commitment-documents'] },
          { label: 'Hírek', icon: 'newspaper', link: '/posts/all', match: ['/posts'] },
        ];
        break;
      case Role.AnthroposophyUser:
        this.tabs = [
          { label: 'Termékek', icon: 'spa', link: '/anthroposophy-products', match: ['/anthroposophy-products'] },
          { label: 'Patikák', icon: 'local_pharmacy', link: '/anthroposophy-subscriptions', match: ['/anthroposophy-subscriptions'] },
        ];
        break;
      case Role.Pharmacy:
      case Role.PartnerProgramPharmacy:
      case Role.PharmacyBasic:
        this.tabs = [
          { label: 'Kezdőlap', icon: 'home', link: '/', match: ['/'] },
          { label: 'Számlák', icon: 'receipt_long', link: '/invoice', match: ['/invoice'] },
          { label: 'Reklamáció', icon: 'report', link: '/claims', match: ['/claims'] },
          { label: 'Hírek', icon: 'newspaper', link: '/posts/all', match: ['/posts'] },
        ];
        break;
      default:
        this.tabs = [];
    }
  }
}
